import {
  analyticsService,
  useAccount,
  useCurrentCompany,
  useFeatureFlag,
  useModeration,
} from "@mytour/platform";
import {useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import {Alert, Button, Modal, ModalBody, ModalHeader} from "reactstrap";
import {useEinvoicingStatus, useMarkNudgeShown} from "../query/einvoicingQueries.js";
import {PennylaneChoiceCallouts} from "./PennylaneChoiceCallouts.js";

// Re-nudge tous les 2 jours tant qu'aucun choix n'est fait (#650, cadence réduite par #1198).
const NUDGE_INTERVAL_MS = 2 * 24 * 60 * 60 * 1000;

// Délai de grâce après la création du compte utilisateur avant le premier nudge (#1225).
const NUDGE_MIN_ACCOUNT_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Modal de nudge au login (#650) — dernière priorité de la cascade
 * `AppModals.tsx`, insérée en dehors de la Récap globale (montée à part
 * dans `RootLayout`, cf. son commentaire). Auto-gérée comme les autres
 * modales du cascade : rend `null` tant que les conditions ne sont pas
 * réunies, pas de prop.
 *
 * Reprend le composant de choix partagé avec le tunnel de création de compte
 * (mêmes libellés, `PennylaneChoiceCallouts`, #651) ; seule la persistance de
 * fréquence (`nudge_last_shown_at`) et la sortie "Plus tard" sont propres au nudge.
 */
export function EinvoicingNudgeModal() {
  const einvoicingEnabled = useFeatureFlag("facturation-electronique");
  const {company} = useCurrentCompany();
  const {account} = useAccount();
  const {isModeration} = useModeration();
  /**
   * Signal de déclenchement (#1225) : compte utilisateur créé depuis au moins 24h —
   * ni l'abonnement ni le SIRET ne conditionnent le nudge (la Récap porte la saisie
   * du SIRET). Le délai de grâce épargne un compte pendant son premier jour.
   */
  const isAccountOldEnough = isAccountOldEnoughForNudge(account?.createdDate ?? null);
  const {einvoicingStatus, isEinvoicingStatusLoading} = useEinvoicingStatus(
    einvoicingEnabled && isAccountOldEnough
  );
  const {markNudgeShown} = useMarkNudgeShown();

  const [isOpen, setIsOpen] = useState(false);
  const [optOutError, setOptOutError] = useState<string | null>(null);
  // La décision d'ouverture se prend UNE fois, au premier chargement du statut —
  // les mises à jour de cache déclenchées par `markNudgeShown` (qui rafraîchit
  // `nudgeLastShownAt` à "maintenant") ne doivent pas refermer une modal déjà
  // ouverte : seule l'action de l'utilisateur (opt-out/Commencer/Plus tard) ferme.
  const hasDecidedRef = useRef(false);

  /**
   * `einvoicingEnabled`/`isAccountOldEnough` gatent aussi CETTE décision, pas seulement le
   * flag `enabled` de la query ci-dessus : un statut déjà en cache pour ce companyId (chargé
   * par un autre call-site sans le même gating — ex. `EinvoicingSettingsPage`, qui active
   * la query sur `einvoicingEnabled` seul) ne doit pas suffire à ouvrir le nudge.
   * `hasDecidedRef` ne se pose QUE quand les deux gates sont vrais : tant que l'account
   * n'est pas chargé (ou que le seuil de 24h n'est pas atteint), la décision reste ouverte
   * pour que le nudge puisse s'afficher dès que le gate devient vrai dans la même session
   * (sans lock prématuré sur un statut en cache chargé avant l'account).
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: `company` est lu pour le tracking analytics seul (identité stable), pas un déclencheur de ré-évaluation
  useEffect(() => {
    if (
      hasDecidedRef.current ||
      !einvoicingEnabled ||
      !isAccountOldEnough ||
      isEinvoicingStatusLoading ||
      !einvoicingStatus
    ) {
      return;
    }
    hasDecidedRef.current = true;
    const isDue = einvoicingStatus.choice === null;
    if (isDue && isNudgeDue(einvoicingStatus.nudgeLastShownAt)) {
      setIsOpen(true);
      if (company) {
        analyticsService.track("einvoicing_nudge_shown", {companyId: company.id});
      }
    }
  }, [einvoicingEnabled, isAccountOldEnough, isEinvoicingStatusLoading, einvoicingStatus]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} backdrop="static" autoFocus={false} centered size="lg">
      <ModalHeader close={<></>}>Vos factures fournisseurs passent à l'électronique</ModalHeader>
      <ModalBody>
        <p className="text-muted">
          Au 1er septembre 2026, votre entreprise doit pouvoir recevoir ses factures par voie
          électronique, via une plateforme agréée.
        </p>
        <p>Comment souhaitez-vous procéder ?</p>

        <PennylaneChoiceCallouts
          onOptOut={handleOptOut}
          onStartPennylane={handleStartPennylane}
          onOptOutError={setOptOutError}
        />
        {optOutError && (
          <Alert color="danger" className="mt-3 mb-0">
            {optOutError}
          </Alert>
        )}

        <div className="text-center">
          <Button color="link" className="text-muted" onClick={handleDismiss}>
            Plus tard
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );

  function handleOptOut() {
    setIsOpen(false);
    toast.success("Choix enregistré.");
    if (company) {
      analyticsService.track("einvoicing_nudge_choice_external", {companyId: company.id});
    }
  }

  function handleStartPennylane() {
    setIsOpen(false);
    if (company) {
      analyticsService.track("einvoicing_nudge_choice_pennylane", {companyId: company.id});
    }
  }

  function handleDismiss() {
    setIsOpen(false);
    /**
     * Seule sortie qui espace la relance suivante (#1195) : un abandon du tunnel doit
     * re-proposer le nudge au refresh suivant, donc `nudgeLastShownAt` n'est marqué que
     * sur ce chemin. En modération, jamais marqué — la fenêtre appartient au client.
     */
    if (!isModeration) {
      /**
       * `mutateAsync` rejette sur échec (contrat React Query v5, `onError` du hook ne
       * l'avale pas) — sans ce `.catch`, un échec réseau remonte en unhandled rejection
       * alors que l'échec est déjà loggé et sans conséquence UI (la modal est déjà fermée).
       */
      markNudgeShown().catch(() => {});
    }
    if (company) {
      analyticsService.track("einvoicing_nudge_dismissed", {companyId: company.id});
    }
  }
}

/**
 * `nudgeLastShownAt` null (jamais montrée) ou plus vieux que 2 jours.
 * Diff en millisecondes plutôt qu'en jours calendaires — évite le piège
 * dayjs `.tz(instance).startOf/add` (offset figé au démarrage du process,
 * cf. #691) : aucune arithmétique calendaire ici, juste une durée écoulée.
 */
export function isNudgeDue(nudgeLastShownAt: string | null): boolean {
  if (nudgeLastShownAt === null) {
    return true;
  }
  return Date.now() - new Date(nudgeLastShownAt).getTime() >= NUDGE_INTERVAL_MS;
}

/**
 * Compte créé il y a au moins 24h — `null` (account pas encore chargé) vaut « pas
 * encore éligible », le gate reste fail-close. Même diff en millisecondes que
 * `isNudgeDue` (cf. son commentaire sur le piège dayjs #691). `createdDate` est typé
 * `Date` mais arrive en string ISO au runtime (axios ne revive pas les dates) —
 * `new Date(...)` couvre les deux.
 */
export function isAccountOldEnoughForNudge(createdDate: Date | null): boolean {
  if (createdDate === null) {
    return false;
  }
  return Date.now() - new Date(createdDate).getTime() >= NUDGE_MIN_ACCOUNT_AGE_MS;
}
